/**
 * LECTEUR DE SÉRIES ENEDIS (DEV-1508 lot 2) — le seul appelant front du lecteur
 * dapi `POST /source/{_id_source}/series` (lot 1a) et de `/source/informations`
 * étendue (lot 1b).
 * ============================================================================
 *
 * INTENTION : la page « Données du compteur » regarde une fenêtre et veut la
 * courbe au bon grain. Ce service traduit cette demande en UN appel dapi, et rend
 * la réponse TELLE QUELLE : colonnaire, avec ses métadonnées. Il ne lisse rien, ne
 * remplit rien, ne somme rien.
 *
 * Trois règles portées par les types eux-mêmes :
 *  • `v[i] === null` = pas non collecté. JAMAIS 0 (décision de Mathieu du 07/09) ;
 *  • soutirage et injection sont deux jeux de séries, un appel chacun, jamais
 *    sommés ni nettés (engagement E5) ;
 *  • l'unité et la NATURE viennent de `meta`, jamais du nom de la variable : le
 *    même `puissanceactive_*_tout.max` vaut des kVA (PMax R66 lue) ou des kW
 *    (enveloppe dérivée du natif). Les deux ne partagent pas un axe.
 *
 * Le service ne rattrape pas les erreurs : l'appelant décide quoi montrer et NE
 * VIDE PAS ce qui est déjà affiché (patron GRDF, `sourceDataGrdf.service.ts`).
 */
import axios from "@core/plugins/axios.plugin"
import type { InformationPDL } from "@types"

/** Ce que `/source/informations` rend : une source, informations imbriquées. */
type SourceInformationsServie = {
    informations?: { pdl?: InformationPDL }
}

// ============================================================================
// VOCABULAIRE DE LA ROUTE (schéma dapi `docs/openapi-data-source-series.yaml`)
// ============================================================================

/** Les deux sens. Jamais une somme, jamais un net (E5). */
export type DirectionEnedis = "soutirage" | "injection"

/** Grains servis par le lecteur. */
export type GrainSeries =
    | "natif"
    | "hourly"
    | "daily"
    | "weekly"
    | "monthly"
    | "periode"

/** Statistiques servies. `min` a été ajoutée au lot 1a. */
export type StatSeries = "mean" | "min" | "max" | "sum" | "last"

/** Provenance d'un point ; `null` quand le pas est absent. */
export type ProvenanceSeries = "raw" | "computed" | "corrected" | "migrated_v1"

/**
 * Nature de la grandeur RÉELLEMENT servie, quand le nom de la variable ne suffit
 * pas à la dire. Deux grandeurs physiquement différentes se cachent sous
 * `puissanceactive_*_tout.max` : ne jamais les comparer ni les additionner.
 */
export type NatureSeries =
    | "pmax_apparente_r66"
    /**
     * Le maximum des puissances MOYENNES sur le pas du compteur, en kW. Le pas
     * est écrit dans le nom (`max_moyennes_30min_active`) parce qu'il change
     * d'un compteur à l'autre : un maximum de moyennes 30 min et un maximum de
     * moyennes 10 min ne sont pas la même grandeur.
     */
    | `max_moyennes_${string}_active`

/** Un point x : horodate, jour civil, ou couple [début, fin] au grain `periode`. */
export type PointX = string | [string, string]

/** Une série colonnaire : `x`, `v` et `t` s'indexent par position. */
export type SerieEnedis = {
    variable: string
    grain: GrainSeries
    stat: StatSeries
    x: PointX[]
    /** `null` = pas non collecté. Jamais 0 pour une absence. */
    v: Array<number | null>
    t: Array<ProvenanceSeries | null>
}

/** Cause de ce qui manque sur une variable réactive. */
export type QualiteSerie = {
    etat: "non_publiee" | "trouee" | "complete"
    points_mesures?: number
    points_attendus?: number
    couverture_pct?: number | null
}

/**
 * Pourquoi une statistique DEMANDÉE n'est pas dans la réponse : le calcul qui
 * l'aurait produite n'est fait que sous une certaine fenêtre (l'enveloppe
 * horaire se calcule sur 31 jours, l'enveloppe journalière sur 92), et la
 * demande la dépassait. La statistique est alors OMISE, pas servie vide.
 *
 * ⚠️ Les trois champs sont facultatifs à la lecture : la page dit ce qu'elle
 * sait, elle ne fabrique pas un nombre que le lecteur n'a pas donné.
 */
export type PlafondApplique = {
    /** Le calcul qui n'a pas été fait, nommé côté lecteur. */
    raison?: string
    /** Fenêtre au-delà de laquelle ce calcul n'est plus fait, en jours. */
    plafond_jours?: number
    /** Fenêtre demandée, en jours. */
    fenetre_jours?: number
}

export type MetaSeries = {
    _id_source: string
    direction: DirectionEnedis
    grain: GrainSeries
    date_from: string
    date_to: string
    fenetre_jours: number
    plafond_fenetre_jours: number | null
    /** Unité par `variable.stat`, jamais par variable seule. */
    unites: Record<string, string>
    /** Nature par `variable.stat`, absente quand le nom suffit. */
    natures?: Record<string, NatureSeries>
    pas_natif: string | null
    pas_natif_par_jour?: Record<string, string>
    /** Par `variable.stat` agrégée à la volée, avec l'origine du calcul. */
    calcule_a_la_lecture: Record<string, string>
    /** Par `variable.stat` OMISE parce qu'un plafond a borné la demande. */
    plafond_applique: Record<string, PlafondApplique>
    /** Heure d'atteinte de la PMax, alignée sur `x`. */
    at?: Record<string, Array<string | null>>
    /** `REEL` / `ESTIME` / `REGU` des périodes de facturation, alignée sur `x`. */
    nature?: Record<string, Array<string | null>>
    /** Points réellement agrégés, aligné sur `x`, par `variable.stat`. */
    n?: Record<string, Array<number | null>>
    /** Vrai quand `n` est inférieur à l'attendu du pas, aligné sur `x`. */
    partiel?: Record<string, Array<boolean | null>>
}

export type ReponseSeries = {
    meta: MetaSeries
    data: SerieEnedis[]
    qualite: Record<string, QualiteSerie>
}

/** Corps de `POST /source/{_id_source}/series`. */
export type DemandeSeries = {
    direction: DirectionEnedis
    /** Noms dataV2, douze au plus (plafond dapi). */
    variables: string[]
    grain: GrainSeries
    stats: StatSeries[]
    /** `YYYY-MM-DD`, bornes incluses. */
    dateFrom: string
    dateTo: string
}

// ============================================================================
// PLAFONDS DU LECTEUR (engagement E8) — repris du lecteur dapi, pas devinés.
// Le front les respecte AVANT d'appeler : un 400 servi à l'écran est un aveu.
// ============================================================================

/** Fenêtre maximale par grain, en jours. `periode` n'en a pas. */
export const PLAFOND_JOURS: Record<GrainSeries, number | null> = {
    natif: 31,
    hourly: 366,
    daily: 730,
    weekly: 730,
    monthly: 730,
    periode: null,
}

/** Index et énergie au grain journalier : trois ans. */
export const PLAFOND_JOURS_INDEX_ENERGIE = 1095

/** Variables par appel. */
export const PLAFOND_VARIABLES = 12

/** Points par série renvoyée. */
export const PLAFOND_POINTS_PAR_SERIE = 40000

/** Grains où la puissance apparente est servie. Jamais au jour. */
export const GRAINS_APPARENTE: GrainSeries[] = ["natif", "hourly"]

/** Familles dont le grain journalier bénéficie du plafond long (1 095 jours). */
export const PREFIXES_FENETRE_LONGUE = [
    "energieactive_",
    "energiereactive_",
    "idx",
    "energie_depassementpuissance",
    "duree_depassementpuissance",
]

/**
 * Plafond de DÉRIVATION, en jours : la fenêtre au-delà de laquelle le lecteur ne
 * refait plus le calcul depuis la courbe native, et OMET la statistique en la
 * nommant dans `meta.plafond_applique` (raison `derivation-depuis-le-natif`).
 *
 * ⚠️ Ce n'est PAS le plafond de la fenêtre de lecture (`PLAFOND_JOURS`) : une
 * fenêtre horaire d'un an est parfaitement lisible, c'est son enveloppe minimum
 * et maximum qui ne se dérive que sur trente et un jours. Confondre les deux,
 * c'était laisser partir une demande de trente-neuf jours pour une fenêtre
 * regardée de trente et un, et lire « non servie sur cette fenêtre » sur une
 * fenêtre qui y avait droit (constat du 11/09 sur staging).
 *
 * LE JOURNALIER EST DÉCOUPÉ AUSSI (DEV-1508 lot 5c, écart T13, DEV-1704).
 *
 * Le lecteur borne l'enveloppe JOURNALIÈRE à 92 jours (règle ADR-016). Tant que
 * le contexte par défaut était une année entière, découper là aurait multiplié
 * par quatre les appels de la vue d'ouverture ; depuis que la page ouvre sur
 * trois mois, l'ouverture ne découpe rien du tout, et c'est seulement en
 * demandant « 1 an » ou « 2 ans » qu'on paie le découpage — pour recevoir une
 * enveloppe au lieu de lire « calcul limité à 92 jours » sur toute la période.
 */
export const PLAFOND_DERIVATION_JOURS: Partial<Record<GrainSeries, number>> = {
    hourly: 31,
    daily: 92,
}

/**
 * Le plafond de dérivation qui s'applique à CETTE demande, ou `null` quand rien
 * n'est dérivé. Seules les statistiques d'enveloppe (`min`, `max`) le subissent :
 * une moyenne horaire se lit dans la feuille stockée, sans plafond.
 */
export function plafondDerivationJours(
    grain: GrainSeries,
    stats: StatSeries[]
): number | null {
    if (!stats.some((stat) => stat === "min" || stat === "max")) return null
    return PLAFOND_DERIVATION_JOURS[grain] ?? null
}

/** Plafond de fenêtre applicable à un grain pour un jeu de variables donné. */
export function plafondJours(
    grain: GrainSeries,
    variables: string[]
): number | null {
    if (
        grain === "daily" &&
        variables.length > 0 &&
        variables.every((v) =>
            PREFIXES_FENETRE_LONGUE.some((p) => v.startsWith(p))
        )
    ) {
        return PLAFOND_JOURS_INDEX_ENERGIE
    }
    return PLAFOND_JOURS[grain]
}

// ============================================================================
// APPELS
// ============================================================================

/**
 * UN appel au lecteur de séries, pour UNE direction. Deux directions = deux
 * appels : elles ne se mélangent jamais dans une même demande.
 */
export async function fetchSeries(
    idSource: string,
    demande: DemandeSeries,
    signal?: AbortSignal
): Promise<ReponseSeries> {
    const { data } = await axios.post<ReponseSeries>(
        `/api/dapi/source/${idSource}/series`,
        {
            direction: demande.direction,
            variables: demande.variables,
            grain: demande.grain,
            stats: demande.stats,
            date_from: demande.dateFrom,
            date_to: demande.dateTo,
        },
        { signal }
    )
    return data
}

/**
 * Informations du compteur pour l'en-tête, avec le pas de la courbe déduit du
 * bucket quand la capacité ne le porte pas (`_cdc_pas=true`, lot 1b). La
 * déduction coûte une lecture : elle ne se demande que pour UNE source.
 *
 * ⚠️ La route ne rend PAS un `InformationPDL` : elle rend une SOURCE, dont les
 * informations du point vivent sous `informations.pdl`. Prendre `data[0]` pour
 * l'objet cherché rendrait un en-tête entièrement vide sur un compteur qui a
 * tout, et rien ne le dirait : les huit faits sont facultatifs par construction.
 * `null` ici veut dire « aucune source servie, ou pas un compteur Enedis ».
 */
export async function fetchInformationsEtendues(
    idSource: string,
    signal?: AbortSignal
): Promise<InformationPDL | null> {
    const {
        data: { data: sources },
    } = await axios.get<{ data: SourceInformationsServie[] }>(
        `/api/dapi/source/informations`,
        {
            params: { _id_source: idSource, _cdc_pas: true },
            signal,
        }
    )
    return sources?.[0]?.informations?.pdl ?? null
}
